/* eslint-disable import/order, complexity */
import { Card, CardBody } from '@nextui-org/react'
import { Icons } from '@/components/icons'

export interface PaymentDetails {
    paymentNumber: string | null
    ipAddress: string
    enrollment: string
    totalAmount: number
    discountAmount: number
    finalAmount: number
    date: string
    time: string
    paymentStatus: string
    paymentStatusDescription: string
    hasGeneratedOrderFromSap?: boolean
}

// Determinar colores basados en el estado del pago
const getStatusColors = (status: string) => {
    switch (status.toUpperCase()) {
        case 'PAGADO':
            return {
                bg: 'bg-green-50',
                border: 'border-green-200',
                headerBg: 'bg-green-500',
                text: 'text-green-800',
                icon: 'text-green-600',
                accent: 'text-green-600'
            }
        case 'PENDIENTE':
            return {
                bg: 'bg-orange-50',
                border: 'border-orange-200',
                headerBg: 'bg-orange-500',
                text: 'text-orange-800',
                icon: 'text-orange-600',
                accent: 'text-orange-600'
            }
        case 'RECHAZADO':
        case 'FALLIDO':
            return {
                bg: 'bg-red-50',
                border: 'border-red-200',
                headerBg: 'bg-red-500',
                text: 'text-red-800',
                icon: 'text-red-600',
                accent: 'text-red-600'
            }
        default:
            return {
                bg: 'bg-gray-50',
                border: 'border-gray-200',
                headerBg: 'bg-gray-500',
                text: 'text-gray-800',
                icon: 'text-gray-600',
                accent: 'text-gray-600'
            }
    }
}

interface PaymentCardProps {
    paymentDetails: PaymentDetails
    className?: string
}

const paymentPending = (
    paymentDetails: PaymentDetails,
    isGeneratedOrder: boolean,
    colors: any,
    hasDiscount: boolean,
    hasPaymentNumber: boolean
) => {
    if (isGeneratedOrder) {
        const colors = getStatusColors(
            paymentDetails.paymentStatus || 'PENDIENTE'
        )

        return (
            <div className={`w-full`}>
                <div className="flex flex-col text-left space-y-4">
                    <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between gap-9">
                            <div
                                className={`${colors.headerBg} text-white px-3 py-2 rounded-lg flex items-center space-x-2`}
                            >
                                <Icons.InfoCircle className="w-5 h-5" />
                                <span className="font-semibold text-sm">
                                    Orden de pago generada
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 font-medium">
                                Referencia:{' '}
                                <span className="font-mono">
                                    {paymentDetails.enrollment}
                                </span>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg border border-gray-100 flex items-start space-x-3">
                            <Icons.Economy className="w-6 h-6 text-green-600 mt-1" />
                            <div>
                                <p className="text-sm text-gray-800 font-medium">
                                    Ya tienes una orden de pago generada en la
                                    plataforma de pagos.
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                    Ingresa a la plataforma de pagos con tu
                                    referencia para consultar el valor y
                                    realizar el pago de tu matrícula.
                                </p>
                                {paymentDetails.paymentStatusDescription && (
                                    <p
                                        className={`text-xs mt-2 ${colors.accent}`}
                                    >
                                        {
                                            paymentDetails.paymentStatusDescription
                                        }
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        /* Layout horizontal compacto para otros estados */
        <div className="flex items-center justify-between gap-4">
            {/* Status y fecha - lado izquierdo */}
            <div className="flex items-center space-x-3">
                <div
                    className={`${colors.headerBg} text-white px-2 py-1 rounded flex items-center space-x-1`}
                >
                    <Icons.Clock className="w-3 h-3" />
                    <span className="font-medium text-xs">
                        {paymentDetails.paymentStatus}
                    </span>
                </div>
                <div className="text-xs text-gray-500">
                    {paymentDetails.date} • {paymentDetails.time}
                </div>
            </div>

            {/* Monto - centro */}
            <div className="text-center">
                {hasDiscount && (
                    <div className="text-gray-400 text-xs line-through">
                        $
                        {(paymentDetails.totalAmount || 0).toLocaleString(
                            'es-CO'
                        )}
                    </div>
                )}
                <div className={`text-lg font-bold ${colors.text}`}>
                    ${(paymentDetails.finalAmount || 0).toLocaleString('es-CO')}
                </div>
                {hasDiscount && (
                    <div className={`text-xs ${colors.accent}`}>
                        Desc: -$
                        {(paymentDetails.discountAmount || 0).toLocaleString(
                            'es-CO'
                        )}
                    </div>
                )}
            </div>

            {/* Detalles - lado derecho */}
            <div className="text-right text-xs space-y-1">
                {hasPaymentNumber && (
                    <div>
                        <span className={`${colors.accent} font-medium`}>
                            Pago:{' '}
                        </span>
                        <span className="font-mono">
                            {paymentDetails.paymentNumber}
                        </span>
                    </div>
                )}
                <div>
                    <span className={`${colors.accent} font-medium`}>
                        Referencia:{' '}
                    </span>
                    <span className="font-mono">
                        {paymentDetails.enrollment}
                    </span>
                </div>
            </div>
        </div>
    )
}

export const PaymentCard = ({
    paymentDetails,
    className = ''
}: PaymentCardProps) => {
    const isGeneratedOrder = paymentDetails.hasGeneratedOrderFromSap
    const colors = getStatusColors(paymentDetails.paymentStatus)
    const isPaid = paymentDetails.paymentStatus.toUpperCase() === 'PAGADO'
    const hasPaymentNumber =
        paymentDetails.paymentNumber !== null &&
        paymentDetails.paymentNumber.trim() !== ''
    const hasDiscount = (paymentDetails.discountAmount || 0) > 0

    return (
        <div className={`w-full ${className}`}>
            <Card className={`shadow-sm ${colors.bg} ${colors.border} border`}>
                <CardBody className={isPaid ? 'p-6' : 'p-3'}>
                    {isPaid ? (
                        /* Diseño grande y específico para PAGADO */
                        <div className="space-y-4">
                            {/* Header con status y fecha */}
                            <div className="flex items-center justify-between">
                                <div
                                    className={`${colors.headerBg} text-white px-3 py-2 rounded-lg flex items-center space-x-2`}
                                >
                                    <Icons.CheckCircle className="w-5 h-5" />
                                    <span className="font-semibold text-sm">
                                        {paymentDetails.paymentStatus}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                    {paymentDetails.date} •{' '}
                                    {paymentDetails.time}
                                </div>
                            </div>

                            {/* Sección de montos centralizada */}
                            <div className="text-center py-4 bg-white rounded-lg border border-gray-100">
                                {hasDiscount && (
                                    <>
                                        <div className="text-gray-400 text-sm line-through mb-1">
                                            $
                                            {(
                                                paymentDetails.totalAmount || 0
                                            ).toLocaleString('es-CO')}
                                        </div>
                                        <div
                                            className={`text-orange-600 text-sm mb-2`}
                                        >
                                            Descuento: -$
                                            {(
                                                paymentDetails.discountAmount ||
                                                0
                                            ).toLocaleString('es-CO')}
                                        </div>
                                    </>
                                )}
                                <div
                                    className={`text-3xl font-bold ${colors.text} mb-1`}
                                >
                                    $
                                    {(
                                        paymentDetails.finalAmount || 0
                                    ).toLocaleString('es-CO')}
                                </div>
                                <div className="text-gray-600 text-sm">
                                    Valor Final a Pagar
                                </div>
                            </div>

                            {/* Detalles del pago en grid */}
                            <div
                                className={`grid ${hasPaymentNumber ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}
                            >
                                {hasPaymentNumber && (
                                    <div className="bg-white p-3 rounded-lg border border-gray-100">
                                        <div
                                            className={`text-xs font-medium ${colors.accent} mb-1`}
                                        >
                                            Número de Pago
                                        </div>
                                        <div className="font-mono text-sm font-semibold">
                                            {paymentDetails.paymentNumber}
                                        </div>
                                    </div>
                                )}
                                <div className="bg-white p-3 rounded-lg border border-gray-100">
                                    <div
                                        className={`text-xs font-medium ${colors.accent} mb-1`}
                                    >
                                        Matrícula
                                    </div>
                                    <div className="font-mono text-sm font-semibold">
                                        {paymentDetails.enrollment}
                                    </div>
                                </div>
                            </div>

                            {/* Mensaje de confirmación prominente */}
                            <div
                                className={`${colors.bg} p-4 rounded-lg border-2 ${colors.border} flex items-center space-x-3`}
                            >
                                <Icons.InfoCircle
                                    className={`w-6 h-6 ${colors.icon}`}
                                />
                                <span
                                    className={`text-sm font-medium ${colors.text}`}
                                >
                                    Matrícula confirmada exitosamente
                                </span>
                            </div>
                        </div>
                    ) : (
                        paymentPending(
                            paymentDetails,
                            isGeneratedOrder || false,
                            colors,
                            hasDiscount,
                            hasPaymentNumber
                        )
                    )}
                </CardBody>
            </Card>
        </div>
    )
}
